import { Container, Graphics } from "pixi.js"
import { IntroBackground } from "./intro_background"
import { GuiManager } from "../gui_manager"
import { ProjectLogo } from "./project_logo"
import { Fader } from "../gui/fader"
import { randomIntBetween } from "game-common/models"
import { BasicSprite } from "../sprites/basic_sprite"
import { LoginCard } from "./login_card"
import { COLORS, METRICS, SCREEN_H, SCREEN_W, drawChip, ensureIntroFont } from "./intro_theme"

type LoginCallback = (username?: string, password?: string) => void

/** Where the wordmark starts before it settles, and how long the slide takes. */
const LOGO_DROP = 120
const LOGO_SLIDE_MS = 340

/**
 * The card's middle. The wordmark settles above ~130 and the socials/fullscreen chips sit at
 * CHROME_Y, so this centres the card in what is left and lets it grow either way.
 */
const CARD_CENTER_Y = 273
const CHROME_Y = SCREEN_H - 40

export class Intro extends Graphics {
    guiManager: GuiManager
    loggingIn: boolean
    landingContainer: Graphics
    container: Container
    socials: Graphics

    private card?: LoginCard

    constructor(guiManager: GuiManager) {
        super()
        this.guiManager = guiManager
    }

    formRemover = () => {
        this.card?.dispose()
        this.card = undefined
        this.container?.removeChildren()
        this.container?.parent?.removeChild(this.container)
        this.container = undefined
    }

    guestLandingRemover = () => {
        this.formRemover()

        this.landingContainer?.removeChildren()
        this.landingContainer?.parent?.removeChild(this.landingContainer)

        this.socials?.removeChildren()
        this.socials?.parent?.removeChild(this.socials)
    }

    loginSuccess = () => {
        this.loggingIn = false
        this.guestLandingRemover()
    }

    /**
     * A rejected sign-in. Returns true when the card took the message inline -- the caller then
     * skips the global screen banner, which used to land across the wordmark.
     */
    loginFailed = (): boolean => {
        this.loggingIn = false

        if (this.card) {
            this.container.visible = true
            this.card.showError("That name and password don't match.")
            return true
        }

        if (this.container) {
            this.container.visible = true
        }
        return false
    }

    init = (doneCallback: LoginCallback) => {
        if (this.loggingIn) {
            return
        }

        // Jost has to be resolvable before the first Text is built; a pixi Text keeps whatever face
        // it rasterized with. ensureIntroFont never rejects, so a slow CDN-less start still gets a
        // login screen, just in Verdana.
        ensureIntroFont().then(() => this.build(doneCallback))
    }

    private build = (doneCallback: LoginCallback) => {
        const landingContainer = new Graphics()
        this.landingContainer = landingContainer

        const introBackgroundGenerator = new IntroBackground()
        const background = introBackgroundGenerator.update()
        background.x = 0
        landingContainer.addChild(background)

        // A touch of scrim so white text and the card's rim hold up over the busiest parts of the
        // forest without hiding the art.
        const scrim = new Graphics()
        scrim.beginFill(COLORS.scrim, 0.28)
        scrim.drawRect(0, 0, SCREEN_W, SCREEN_H + 40)
        scrim.endFill()
        landingContainer.addChild(scrim)

        const butterflies = introBackgroundGenerator.bufferFlies()
        butterflies.x = 0
        landingContainer.addChild(butterflies)

        const introGenerator = new ProjectLogo()
        const intro = introGenerator.update(() => {
            this.slideLogo(intro)

            new Fader(background as Graphics, {
                direction: "in",
                minAlpha: 1.0,
                fadeAlphaDecrement: 0.005,
                releaseCallback: () => {
                    if (this.container) {
                        this.container.visible = true
                        this.card?.focus()
                    }
                },
            }).update()

            this.container = this.buildLoginContainer(doneCallback)
            this.container.visible = false
            this.guiManager.stage.addChild(this.container)

            this.socials = this.buildSocials(this.guiManager.stage)
        })

        intro.x = Math.round((SCREEN_W - introGenerator.calcWidth()) / 2)
        intro.y = LOGO_DROP

        landingContainer.addChild(intro)
        this.guiManager.stage.addChild(landingContainer)
    }

    /** Ease the wordmark up to its resting place (the old version stepped 1px on a 1ms timer). */
    private slideLogo = (intro: Container) => {
        const started = Date.now()
        const tick = () => {
            const t = Math.min(1, (Date.now() - started) / LOGO_SLIDE_MS)
            const eased = 1 - Math.pow(1 - t, 3)
            intro.y = Math.round(LOGO_DROP * (1 - eased))
            if (t < 1) {
                requestAnimationFrame(tick)
            }
        }
        requestAnimationFrame(tick)
    }

    private buildLoginContainer = (doneCallback: LoginCallback): Container => {
        const container = new Container()

        const guestName = "Guest " + randomIntBetween(1, 10000)

        const card = new LoginCard({
            guestName,
            centerY: CARD_CENTER_Y,
            onPlay: (playerName: string) => {
                container.visible = false
                setTimeout(() => doneCallback(playerName), 250)
            },
            onLogin: (username: string, password: string) => {
                this.loggingIn = true
                container.visible = false
                doneCallback(username, password)
            },
        })

        // y is the card's own business -- it re-centres itself on CARD_CENTER_Y after each layout.
        card.x = Math.round((SCREEN_W - METRICS.cardWidth) / 2)

        container.addChild(card)
        this.card = card

        return container
    }

    buildSocials = (parent?: Container): Graphics => {
        const allContainer = new Graphics()
        if (parent) {
            parent.addChild(allContainer)
        }

        const socialContainer = new Graphics()
        socialContainer.x = 12
        socialContainer.y = CHROME_Y

        const socialMedia = this.guiManager.socialMedia()
        // socialMedia() positions its own row for the in-game HUD; the intro parks it in a chip.
        socialMedia.y = 0
        socialMedia.x = 0

        drawChip(socialContainer, socialMedia.width + 20, 34)
        socialMedia.x = 10
        socialMedia.y = Math.round((34 - socialMedia.height) / 2)
        socialContainer.addChild(socialMedia)
        allContainer.addChild(socialContainer)

        const fullscreenChip = new Graphics()
        drawChip(fullscreenChip, 34, 34)
        fullscreenChip.x = SCREEN_W - 34 - 12
        fullscreenChip.y = CHROME_Y

        const expandFullScreen = new BasicSprite({ name: "fullscreen.png", scale: 0.55 })
        expandFullScreen.x = 17
        expandFullScreen.y = 17
        fullscreenChip.addChild(expandFullScreen)

        fullscreenChip.interactive = true
        fullscreenChip.cursor = "pointer"
        let expanded = false
        const handler = () => {
            expanded = !expanded
            if (expanded) {
                const elem = document.documentElement as any
                if (elem.requestFullscreen) {
                    elem.requestFullscreen()
                } else if (elem.webkitRequestFullscreen) {
                    /* Safari */
                    elem.webkitRequestFullscreen()
                } else if (elem.msRequestFullscreen) {
                    /* IE11 */
                    elem.msRequestFullscreen()
                }
            } else {
                const doc = document as any
                if (doc.exitFullscreen) {
                    doc.exitFullscreen()
                } else if (doc.webkitExitFullscreen) {
                    /* Safari */
                    doc.webkitExitFullscreen()
                } else if (doc.msExitFullscreen) {
                    /* IE11 */
                    doc.msExitFullscreen()
                }
            }
        }
        fullscreenChip.on("click", handler)
        fullscreenChip.on("tap", handler)

        allContainer.addChild(fullscreenChip)

        return allContainer
    }
}
